import { cn } from "@/lib/utils";
import { useState } from "react";
import { FileParserTuple } from "../../hooks/use-file-parser";
import { useExpertSelection } from "../Expert/selection/ExpertSelectionContext";
import { GroupFolderSelector } from "../GroupFolderSelector/GroupFolderSelector";
import { Column } from "../ui/flex";
import {
  AIChatInputActions,
  AIChatInputActionsProps,
} from "./AIChatInputActions";
import { AIChatInputFileChips } from "./AIChatInputFileChips";
import { TipTapTextarea } from "./TipTapTextarea";

interface AIChatInputProps
  extends Pick<
    AIChatInputActionsProps,
    | "capabilities"
    | "modelProfileUuid"
    | "onModelProfileUuidChange"
    | "onCapabilitiesChange"
  > {
  placeholder?: string;
  onSubmit?: (
    prompt: string,
    expertUuid: string | null,
    folderUuids: string[],
  ) => void;
  onCancel?: () => void;
  isPending?: boolean;
  files?: File[];
  onFileSelect?: () => void;
  filePickerElement?: React.ReactNode;
  onFileRemove?: (file: File) => void;
  enableCommand?: boolean;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  foldersGroupUuid?: string | null;
  tuples?: FileParserTuple[];
  className?: string;
  onSelectFolder?: (folders: string[]) => void;
  expectExpertSelection: boolean;
}

export const AIChatInput: React.FC<AIChatInputProps> = ({
  placeholder = "Ask me anything you want to search or brainstorm",
  onSubmit,
  onCancel,
  isPending = false,
  files = [],
  onFileSelect,
  filePickerElement,
  onFileRemove,
  enableCommand = true,
  disabled = false,
  value: controlledValue,
  onChange,
  foldersGroupUuid = null,
  tuples,
  className,
  onSelectFolder,
  expectExpertSelection,

  // AiChatInputActions props
  capabilities,
  modelProfileUuid,
  onCapabilitiesChange,
  onModelProfileUuidChange,
}) => {
  const [internalPrompt, setInternalPrompt] = useState("");

  const isControlled = controlledValue !== undefined;
  const prompt = isControlled ? controlledValue : internalPrompt;
  const setPrompt = isControlled ? onChange! : setInternalPrompt;
  const [folderUuids, setFolderUuids] = useState<string[]>([]);
  const [showSelectedFolders, setShowSelectedFolders] = useState(false);

  const { selectedExpertUuids } = useExpertSelection();

  const handleSubmit = () => {
    if (!prompt.trim() || isPending || disabled) {
      return;
    }

    if (!selectedExpertUuids.size) {
      return;
    }

    onSubmit?.(prompt, selectedExpertUuids.values().next().value!, folderUuids);

    if (!isControlled) {
      setInternalPrompt("");
    }
  };

  const handleFolderSelectionChange = (folders: string[]) => {
    setFolderUuids(folders);
    onSelectFolder?.(folders);
  };

  const isSubmitDisabled =
    !prompt.trim() ||
    isPending ||
    disabled ||
    tuples?.some(
      (t) => t.server && t.server.is_uploaded && t.server.isParsing,
    ) ||
    (expectExpertSelection && selectedExpertUuids.size === 0);

  return (
    <div
      className={cn(
        "relative flex flex-col",
        "gap-4 p-3",
        "bg-background border-grey-200 rounded-[10px] border-1",
        "transition-[background-color,box-shadow] duration-500",
        { "bg-background-1": isPending },
        { "shadow-lg": !isPending },
        className,
      )}
    >
      <TipTapTextarea
        value={prompt}
        onChange={setPrompt}
        onSubmit={handleSubmit}
        placeholder={placeholder}
        disabled={disabled || isPending}
        enableCommand={enableCommand}
      />

      <AIChatInputActions
        onFileSelect={onFileSelect}
        filePickerElement={filePickerElement}
        onSubmit={onSubmit ? handleSubmit : undefined}
        onCancel={onCancel}
        isPending={isPending}
        isSubmitDisabled={isSubmitDisabled}
        disabled={disabled}
        selectedFolders={foldersGroupUuid !== null ? folderUuids.length : null}
        onToggleFolderSelection={() =>
          setShowSelectedFolders(!showSelectedFolders)
        }
        capabilities={capabilities}
        onCapabilitiesChange={onCapabilitiesChange}
        modelProfileUuid={modelProfileUuid}
        onModelProfileUuidChange={onModelProfileUuidChange}
      />

      {onFileRemove && files.length > 0 && (
        <Column className={cn("gap-2")}>
          <AIChatInputFileChips
            files={files}
            onFileRemove={onFileRemove}
            tuples={tuples}
          />
        </Column>
      )}

      {foldersGroupUuid !== null && (
        <Column className={cn("gap-2 p-2", { hidden: !showSelectedFolders })}>
          <h3 className="text-foreground-5 text-xs">Folders</h3>
          <GroupFolderSelector
            showEmpty={false}
            groupUuid={foldersGroupUuid}
            kitUuid={null}
            onSelectionChange={handleFolderSelectionChange}
          />
        </Column>
      )}
    </div>
  );
};
